/**
 * DEV-1393 — enable muted autoplay on YouTube video embeds.
 *
 * YouTube is the only oEmbed video provider (NewsController), and its oEmbed
 * iframe src carries no autoplay param, so feed videos sit paused. Browsers
 * only permit autoplay when the video is muted, so we set autoplay=1&mute=1
 * (plus playsinline=1 so iOS plays inline rather than going fullscreen) and
 * make sure the iframe advertises allow="autoplay".
 *
 * Apply to the RAW embed HTML *before* sanitizeEmbed(); the sanitizer keeps the
 * modified src and the `allow` attribute (both allowlisted in sanitizeHtml.ts).
 * Non-YouTube or non-iframe markup is returned unchanged.
 */
export function withYouTubeAutoplay(html: string | null | undefined): string {
  if (!html) return '';
  // Only touch YouTube embed iframes; leave anything else untouched.
  if (!/youtube(?:-nocookie)?\.com\/embed\//i.test(html)) return html;
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return html;

  try {
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    const iframe = doc.querySelector('iframe');
    const src = iframe?.getAttribute('src');
    if (!iframe || !src) return html;

    const url = new URL(src, 'https://www.youtube.com');
    url.searchParams.set('autoplay', '1');
    url.searchParams.set('mute', '1');
    url.searchParams.set('playsinline', '1');
    iframe.setAttribute('src', url.toString());

    // Merge autoplay into the existing allow policy without dropping the rest.
    const allow = iframe.getAttribute('allow') || '';
    if (!/\bautoplay\b/i.test(allow)) {
      iframe.setAttribute('allow', allow ? `${allow}; autoplay` : 'autoplay');
    }

    return iframe.outerHTML;
  } catch {
    // Malformed markup — fall back to the original, sanitizer still runs.
    return html;
  }
}
